import React, { useState, useCallback } from 'react'
import styled, { css } from 'styled-components'
import type { SelectMultipleProps as BaseSelectMultipleProps } from '@ur-solutions/react-components'
import { getUserLabel } from '@/modules/users/util'
import { useQuery } from '@apollo/client'
import { SIMPLE_USERS_QUERY } from '@/modules/users/queries'
import { useDebounce } from '@/utils/hooks'
import type { SelectMultipleOnSelectAll } from './SelectMultiple';
import { SelectMultiple } from './SelectMultiple'
import { useTranslate } from '@ur-solutions/react-hooks'
import { ZIndexRange } from '@/types/style'
import type { DropdownNames } from './types'
import { Field } from '../../modules/companies'
import { getNodes } from '@/utils/graphql'
import type { SimpleUserNode } from '@/modules/users'

const User = styled.div`
  display: flex;
  align-items: center;

  span.name {
    margin-left: 0.6rem;
  }
`

export const SelectMultipleRelativeWrapper = styled.div<{ width: string }>`
  position: relative;
  width: ${props => props.width};
`

export const SelectMultipleWrapper = styled.div<{
  open: boolean
  numSelectedText: string
  compact: boolean
  width: string
}>`
  width: ${props => props.width};
  ${props =>
    !props.open
      ? css`
          ${props.compact &&
          `
          .--select-display-inner > :not(:first-child),
          .--select-display-selected > i,
          .--select-display-selected span { display: none; }
          .--select-display-selected :after { content: '${props.numSelectedText}'; }
        `}
        `
      : `position: absolute; z-index: ${ZIndexRange.Dropdowns};`}
`

export interface SelectMultipleProps
  extends BaseSelectMultipleProps<string, any> {
  compact?: boolean
  grayed?: boolean
  dropdownPageName?: DropdownNames
  withSelectAll?: boolean
  filterSalaryResponsible?: boolean
  filterLeaderGroup?: boolean
  hideItem?: (item: SimpleUserNode) => boolean
  onSelectAll?: SelectMultipleOnSelectAll
  isDirect?: boolean
}

export const SelectMultipleUsers: React.FC<
  Omit<SelectMultipleProps, 'options'>
> = ({
  selected,
  compact = true,
  dropdownPageName,
  width = '300px',
  grayed = true,
  filterSalaryResponsible = false,
  filterLeaderGroup = false,
  hideItem,
  isDirect = false,
  onChange,
  ...props
}) => {
  const [open, setOpen] = useState<boolean>(false)
  const [query, setQuery] = useState<string>('')
  const debouncedQuery = useDebounce(query)

  const translations = useTranslate({
    nUsers: ['users.n-users', { n: 0 }],
    selectUsers: 'common.select-users',
    selectLeaderGroups: 'common.select-leader-groups',
  })

  const { data } = useQuery(SIMPLE_USERS_QUERY, {
    variables: {
      q: debouncedQuery,
      orderBy: 'lastName',
      dropdown: dropdownPageName,
      filterSalaryResponsible,
      filterLeaderGroup,
    },
    fetchPolicy: 'cache-and-network',
  })

  const getLabel = useCallback((user: SimpleUserNode) => {
    const name = getUserLabel(user)

    return (
      <User>
        <span className="name">{name}</span>
      </User>
    )
  }, [])

  const users = getNodes(data?.allUsers)
  const visibleUsers = hideItem ? users.filter(user => !hideItem(user)) : users
  const options =
    visibleUsers.map(user => ({
      value: user.id,
      label: getLabel(user),
    })) ?? []

  return (
    <Field>
      {isDirect ? <label>{translations.selectUsers}</label> : null}

      <SelectMultipleRelativeWrapper width={width}>
        <SelectMultipleWrapper
          open={open}
          numSelectedText={translations.nUsers({ n: selected.length })}
          compact={compact}
          width={width}
        >
          <SelectMultiple
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            selected={selected}
            options={options}
            onChange={onChange}
            searchable="custom"
            onFilterChange={setQuery}
            fullWidth
            grayed={grayed}
            {...props}
          />
        </SelectMultipleWrapper>
      </SelectMultipleRelativeWrapper>
    </Field>
  )
}
